<script setup lang="ts">
import { t } from "@/plugins/i18n";

const {
	label,
	checked,
	indeterminate = false,
	toggleable = true,
	dimmed = false,
	isolated = false,
	allowOnly = true,
	canDrill = false,
	parentText = null,
	parentTooltip = null,
	depth = 0,
} = defineProps<{
	label: string;
	depth?: number;
	checked: boolean;
	indeterminate?: boolean;
	toggleable?: boolean;
	dimmed?: boolean;
	isolated?: boolean;
	allowOnly?: boolean;
	canDrill?: boolean;
	parentText?: string | null;
	parentTooltip?: string | null;
}>();

const emit = defineEmits<{
	(event: "toggle", activation: MouseEvent | KeyboardEvent): void;
	(event: "only"): void;
	(event: "drill"): void;
}>();

const drillOrToggle = (activation: MouseEvent | KeyboardEvent) => {
	if (canDrill) {
		emit("drill");
	} else {
		emit("toggle", activation);
	}
};
</script>
<template>
	<div class="filter-row" :class="{ dimmed }" @click="drillOrToggle">
		<v-checkbox-btn
			:model-value="checked"
			:indeterminate="indeterminate"
			:disabled="!toggleable"
			density="compact"
			hide-details
			class="check"
			@click.stop="emit('toggle', $event)"
		/>
		<div
			class="main"
			:class="{ drillable: canDrill }"
			:style="{ paddingLeft: `${depth * 1.125}rem` }"
			:role="canDrill ? 'button' : undefined"
			:tabindex="canDrill ? 0 : undefined"
			@keydown.enter.prevent="drillOrToggle"
			@keydown.space.prevent="drillOrToggle"
		>
			<span class="label text-body-2">{{ label }}</span>
			<span
				v-if="parentText"
				class="parents text-caption"
				v-tooltip="parentTooltip ?? parentText"
			>
				{{ parentText }}
			</span>
			<v-icon
				v-if="canDrill"
				class="drill"
				icon="fa: fa-regular fa-chevron-right"
				size="12"
				v-tooltip="t('filterDialog.hierarchy.drill')"
			/>
		</div>
		<div class="actions" @click.stop>
			<slot name="actions">
				<v-btn
					v-if="allowOnly"
					variant="text"
					size="small"
					density="comfortable"
					class="action"
					@click="emit('only')"
				>
					{{
						isolated
							? t("filterDialog.hierarchy.all")
							: t("filterDialog.hierarchy.only")
					}}
				</v-btn>
			</slot>
		</div>
	</div>
</template>
<style scoped lang="scss">
.filter-row {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 0.25rem;
	min-height: 2.125rem;
	padding-inline: 0.5rem 0.75rem;
	user-select: none;

	&:hover {
		background: rgb(var(--color-secondary));
	}

	&.dimmed .label,
	&.dimmed .parents {
		opacity: 0.8;
	}

	.actions {
		display: flex;
		flex: 0 0 auto;
		gap: 0.25rem;
		visibility: hidden;
	}

	&:hover .actions {
		visibility: visible;
	}

	.drill {
		opacity: 0;
		color: rgb(var(--color-on-background));
	}

	&:hover .drill {
		opacity: 1;
	}

	// Hidden rather than unmounted: the icon carries a tooltip, and adding or
	// removing it as rows are excluded churns the list mid-patch.
	&.dimmed .drill {
		visibility: hidden;
	}
}

.check {
	flex: 0 0 auto;
}

.main {
	flex: 0 1 auto;
	display: flex;
	align-items: center;
	gap: 0.375rem;
	min-width: 0;

	&.drillable {
		cursor: pointer;
	}

	&:focus-visible {
		outline: 1px solid rgb(var(--color-info));
		outline-offset: -1px;
	}
}

.label {
	flex: 0 1 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.parents {
	flex: 0 3 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: rgb(var(--color-on-background));
}

.action {
	min-width: 3.25rem;
	text-transform: none;
	letter-spacing: 0;
	color: rgb(var(--color-on-background));
}
</style>
